import { Injectable } from '@angular/core';
import * as sphereKnn from 'sphere-knn';

import { TramApiService } from '../tram-api.service';
import { Route, BuiltRoute, EnhancedBuiltRoute } from '../core/route';
import { StopCollector } from './stop-collector';
import { TramCollector } from './tram-collector';
import { RailCollector } from './rail-collector';
import { LeafletTram, Tram } from '../core/tram';
import { Segment, EnhancedSegment } from '../core/segment';
import { Coordinate } from '../core/coordinate';
import { LeafletRail, SegmentedLeafletRail } from '../core/rail';
import { Stop, StopSelectionType, LeafletStop } from '../core/stop';
import { LiteEvent } from './index';

@Injectable()
export class RouteCollector {
    private eventCounter: number = 0;
    private _routes: Map<number, Route>;
    get routes(): Route[] {
        let routesInternal: Route[] = [];

        if (this._routes !== undefined)
            this._routes.forEach(r => routesInternal.push(r));

        return routesInternal;
    }

    private readonly _onAfterInitialize = new LiteEvent<any>();
    private readonly _onBeforeSelect = new LiteEvent<any>();
    private readonly _onAfterSelect = new LiteEvent<any>();
    private readonly _onBeforeUnselectDeeply = new LiteEvent<EnhancedBuiltRoute>();
    private readonly _onBeforeUnselect = new LiteEvent<any>();
    private readonly _onAfterUnselect = new LiteEvent();
    private readonly _onAfterBuildSegmentedRoute = new LiteEvent<EnhancedBuiltRoute>();

    onBeforeInitialize: () => void;
    get afterInitialize() { return this._onAfterInitialize.expose(); }
    get beforeSelect() { return this._onBeforeSelect.expose(); }
    get afterSelect() { return this._onAfterSelect.expose(); }
    get beforeUnselectDeeply() { return this._onBeforeUnselectDeeply.expose(); }
    get beforeUnselect() { return this._onBeforeUnselect.expose(); }
    get afterUnselect() { return this._onAfterUnselect.expose(); }
    get afterBuildSegmentedRoute() { return this._onAfterBuildSegmentedRoute.expose(); }

    constructor(
        private api: TramApiService,
        private stopCollector: StopCollector,
        private railCollector: RailCollector,
        private tramCollector: TramCollector) {
        this._routes = new Map<number, Route>();
    }

    init() {
        this._routes.clear();

        if (this.onBeforeInitialize)
            this.onBeforeInitialize();

        return this.api.getRoutes(0, 0)
            .then(routes => {
                this.railCollector.afterInitialize.on(_ => {
                    this.eventCounter++;
                    this.onAfterInitialization();
                });
                this.stopCollector.afterInitialize.on(_ => {
                    this.eventCounter++;
                    this.onAfterInitialization();
                });

                this.railCollector.initByRoutes(routes);
                this.stopCollector.initByRoutes(routes);

                return routes;
            })
            .then(routes => {
                routes.forEach(r => this._routes.set(r.id, r));

                this.eventCounter++;
                this.onAfterInitialization();

                return this._routes;
            });
    }

    get(routeId: number): Route;
    get(tram: Tram): Route;
    get(object: any): Route {
        let id = typeof (object) === 'number'
            ? object
            : (object as Tram).routeId;

        if (!this._routes.has(id))
            return Route.empty;

        let route = this._routes.get(id);
        return route;
    }
    getByStop(stopId: number): Route[] {
        let filteredRoutes = this.routes.filter(r => {
            if (r.allStopIds.find(id => id === stopId))
                return true;

            return false;
        })
        return filteredRoutes;
    }
    getByRail(railId: number): Route[] {
        let filteredRoutes = this.routes.filter(r => {
            if (r.allRailIds.find(id => id === railId))
                return true;

            return false;
        })
        return filteredRoutes;
    }

    getSegmentedRoutes(startStopId: number, finishStopId: number): Promise<EnhancedBuiltRoute[]> {
        return this.api.getBuiltRoute(startStopId, finishStopId, 0, 0)
            .then(routes => {
                let builtRoutes = routes.map(r => {
                    let builtRoute = new EnhancedBuiltRoute(r);
                    builtRoute.segments = r.segments.map(s => {
                        let segment = new EnhancedSegment(s);
                        segment.route = this.get(s.routeId);
                        // this.scheduleCollector.get(s.scheduleId).then(data => {
                        //     segment.schedule = data;
                        // });

                        let startStop = this.stopCollector.get(s.startInfo.stopId);
                        let finishStop = this.stopCollector.get(s.finishInfo.stopId);
                        segment.stops.push(startStop);
                        segment.stops.push(finishStop);

                        let stopIds = this.tryGetStopIdsOfSegment(segment);
                        segment.stops = [];
                        for (let id of stopIds) {
                            segment.stops.push(this.stopCollector.get(id));
                        }

                        let rails: SegmentedLeafletRail[] = [];
                        let isReverse = this.isSegmentReverse(segment);
                        if (isReverse !== null) {
                            rails = this.railCollector.get(segment, isReverse);
                            segment.rails = rails;
                        }

                        return segment;
                    });

                    return builtRoute;
                });

                return builtRoutes;
            });
    }

    select(tram: Tram): void;
    select(routeId: number, isReverse: boolean): void;
    select(route: Route, isReverse: boolean): void;
    select(builtRoute: EnhancedBuiltRoute): void;
    select(...objects: any[]) {
        let route: Route;
        let isReverse: boolean;

        if (typeof (objects[0]) === 'number') {
            isReverse = objects[1];
            if (!this._routes.has(objects[0]))
                return;

            route = this._routes.get(objects[0]);
        } else if (objects[0] instanceof Route) {
            isReverse = objects[1];
            route = objects[0];
        } else if (objects[0] instanceof Tram) {
            let tram = objects[0] as Tram;
            if (!this._routes.has(tram.routeId)) {
                this.unselect();
                return;
            }

            route = this._routes.get(tram.routeId);
            isReverse = tram.isReverseTrip;
        } else if (objects[0] instanceof EnhancedBuiltRoute) {
            let builtRoute = objects[0] as EnhancedBuiltRoute;
            this.unselect();

            this.buildRoute(builtRoute);
            return;
        }

        this.unselect();

        if (this._onBeforeSelect)
            this._onBeforeSelect.trigger({ isReverse: isReverse, route: route });

        route.isSelected = true;
        this.stopCollector.select(route, isReverse);
        this.railCollector.select(route, isReverse);

        if (this._onAfterSelect)
            this._onAfterSelect.trigger({ isReverse: isReverse, route: route });
    }

    unselect() {
        let selectedRoute = this.routes.filter(r => r.isSelected)[0];
        let isReverse: boolean | null;
        if (selectedRoute) {
            let stop = this.stopCollector.get(selectedRoute.reverseStopIds[0]) as LeafletStop;
            if (stop.isSelected) {
                isReverse = true;
            } else {
                stop = this.stopCollector.get(selectedRoute.stopIds[0]) as LeafletStop;
                if (stop.isSelected)
                    isReverse = false;
            }
        }

        if (this._onBeforeUnselect)
            this._onBeforeUnselect.trigger({ selectedRoute: selectedRoute, isReverse: isReverse });

        this.routes.forEach(r => r.isSelected = false);
        this.stopCollector.unselect();
        this.railCollector.unselect();
        this.tramCollector.unselect();

        if (this._onAfterUnselect)
            this._onAfterUnselect.trigger();
    }
    unselectDeeply(route?: EnhancedBuiltRoute) {
        if (this._onBeforeUnselectDeeply)
            this._onBeforeUnselectDeeply.trigger(route);
        if (route) {
            route.isSelected = false;
        }
        this.stopCollector.unselect(true);
    }

    enable(): void;
    enable(route: Route): void;
    enable(...objects: any[]) {
        if (objects.length === 0) {
            this._routes.forEach(r => {
                this.stopCollector.enable(r.allStopIds);
                this.railCollector.enable(r.allRailIds);
                this.tramCollector.enable(r);
            });
            return;
        }
        let route = objects[0] as Route;

        if (!this._routes.has(route.id))
            return;

        this.stopCollector.enable(route.allStopIds);
        this.railCollector.enable(route.allRailIds);
        this.tramCollector.enable(route);
    }

    disable(): void;
    disable(route: Route): void;
    disable(...objects: any[]) {
        if (objects.length === 0) {
            this._routes.forEach(r => {
                this.stopCollector.disable(r.allStopIds);
                this.railCollector.disable(r.allRailIds);
                this.tramCollector.disable(r);
            });
            return;
        }
        let route = objects[0] as Route;

        if (!this._routes.has(route.id))
            return;

        if (route.isSelected)
            this.unselect();

        let disabledStopIds: number[] = route.allStopIds;
        let disabledRailIds: number[] = route.allRailIds;

        // calc stops based on common stops
        // calc rails based on common rails
        this.routes.forEach(_r => {
            if (_r.id === route.id)
                return;

            if (!_r.isEnabled)
                return;

            disabledStopIds = disabledStopIds.filter(id => _r.allStopIds.indexOf(id) === -1);
            disabledRailIds = disabledRailIds.filter(id => _r.allRailIds.indexOf(id) === -1);
        });

        this.stopCollector.disable(disabledStopIds);
        this.railCollector.disable(disabledRailIds);

        this.tramCollector.disable(route);
    }

    private buildRoute(builtRoute: EnhancedBuiltRoute) {
        let segments = builtRoute.segments;
        segments.forEach((s, index) => {
            let selectionType: StopSelectionType;
            if (segments.length === 1) {
                selectionType = StopSelectionType.StartEnd;
            } else if (index === 0) {
                selectionType = StopSelectionType.Start;
            } else if (index === segments.length - 1) {
                selectionType = StopSelectionType.End;
            } else {
                selectionType = StopSelectionType.GoBetween;
            }
            let stopIds = s.stops.map(s => s.id);
            this.stopCollector.select(stopIds, selectionType);
        });

        builtRoute.isSelected = true;

        if (this._onAfterBuildSegmentedRoute)
            this._onAfterBuildSegmentedRoute.trigger(builtRoute);
    }

    private tryGetStopIdsOfSegment(segment: EnhancedSegment): number[] {
        if (!segment.startStop || !segment.finishStop)
            return [];

        let isReverse = this.isSegmentReverse(segment);
        if (isReverse === null)
            return [];

        let segmentedStopIds: number[] = [];
        if (isReverse) {
            segmentedStopIds = this.getStopIdsOfSegmentInternal(segment, segment.route.reverseStopIds);
        } else {
            segmentedStopIds = this.getStopIdsOfSegmentInternal(segment, segment.route.stopIds);
        }
        return segmentedStopIds;
    }

    private getStopIdsOfSegmentInternal(segment: EnhancedSegment, stopIds: number[]): number[] {
        let segmentedStopIds: number[] = [];

        let startStopIndex = stopIds.indexOf(segment.startStop.id);
        let finishStopIndex = stopIds.indexOf(segment.finishStop.id);

        segmentedStopIds = stopIds.slice(startStopIndex, finishStopIndex + 1);
        return segmentedStopIds;
    }

    private isSegmentReverse(segment: EnhancedSegment): boolean {
        let stopIds = segment.route.stopIds;

        let startStopIndex = stopIds.indexOf(segment.startStop.id);
        let finishStopIndex = stopIds.indexOf(segment.finishStop.id);

        if (startStopIndex < 0 || finishStopIndex < 0) {
            stopIds = segment.route.reverseStopIds;
            startStopIndex = stopIds.indexOf(segment.startStop.id);
            finishStopIndex = stopIds.indexOf(segment.finishStop.id);

            if (startStopIndex < 0 || finishStopIndex < 0) {
                return null;
            }

            return true;
        }

        return false;
    }

    private onAfterInitialization() {
        if (this.eventCounter === 3) {
            if (this._onAfterInitialize)
                this._onAfterInitialize.trigger();

            this.eventCounter = 0;
        }
    }
}


// WEBPACK FOOTER //
// D:/programming/Чижик_Api_Site_Sources-Electroline/front/src/app/common/route-collector.ts